/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import { CUSTOM_DASHBOARD_INTERNAL_TYPES } from '@linearb/linjs-common';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import QueryString from 'query-string';

import * as TrackingService from 'common/services/tracking.service';
import { getDefaultIterations } from 'common/services/iterations.service';
import { TRACKING } from 'common/constants/constants';
import { isMulti, orderIterations } from 'common/utils/utils';
import {
  reportedMetricsModalOptions,
  selectMetricsModalOption
} from 'modals/modal-options-factory';
import { getServicesSelector } from 'state/selectors/services';

import useCustomDashboardMetrics from './useCustomDashboardMetrics';
import { GRANULARITY } from '../consts';
import { isEqualAllTeams } from '../Filters/TeamsDropdown';
import { EMPTY_DEFAULT_REPORT } from '../../../reportsConfiguration';
import { getGranularityByDates } from '../custom-report.util';
import { useLocation } from 'react-router';

const CUSTOM_REPORT_WIDGET_CATEGORIES = {
  CUSTOM_PM_METRICS: 'Custom PM Metrics',
  REPORTED: 'Reported Metrics'
};
const customReportCategories = [
  CUSTOM_REPORT_WIDGET_CATEGORIES.CUSTOM_PM_METRICS,
  CUSTOM_REPORT_WIDGET_CATEGORIES.REPORTED
];

const useCustomReport = ({ report, filters, gitScopeToggle }) => {
  const services = useSelector(getServicesSelector);
  const location = useLocation();
  const isTeamGoals =
    report?.internal_type === CUSTOM_DASHBOARD_INTERNAL_TYPES.TEAM_GOALS;
  const isCustomPMMetrics =
    report?.internal_type === CUSTOM_DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS;
  const isCustomReport = !isEmpty(report?.definition?.filters);

  const [editableReport, setEditableReport] = useState({ ...report });
  const [editableFilters, setEditableFilters] = useState(
    report.definition.filters
  );
  const editableReportDataChangeRef = useRef([]);
  const [localIterations, setLocalIterations] = useState([]);

  const {
    metrics,
    metricsTotal,
    availableMetrics,
    loadingMetrics,
    timeRange,
    fetchNewMetricsData,
    setLoadingMetrics,
    exportMetricsV3Data,
    showAICodeReviewEmptyState
  } = useCustomDashboardMetrics({
    filters,
    report: editableReport,
    iterations: localIterations
  });

  useEffect(() => {
    if (!isEmpty(report) && !isEqual(editableReport, report)) {
      setEditableReport(report);
      setEditableFilters(report.definition.filters);
    }
    if (isTeamGoals) {
      setTimeout(() => {
        filters.setSelectedService([]);
        filters.setSelectedLabels([]);
        filters.setSelectedRepo([]);
        filters.setSelectedContributor(null);
      }, 200);
    }
  }, [report]);

  useEffect(() => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });

    if (search.isCopyLink && search.isCopyLink.toLocaleLowerCase() === 'true') {
      return;
    }

    if (isCustomReport) {
      return filters.initCustomReportFilters(report);
    }
    return filters.restoreFiltersFromStorage({ isTeamGoals, report });
  }, [report.definition.filters]);

  useEffect(() => {
    if (
      !isEmpty(filters) &&
      !isCustomReport &&
      !isTeamGoals &&
      !isCustomPMMetrics
    ) {
      filters.saveFiltersOnStorage();
    }
  }, [filters]);

  const updateTeamIterationsAndGranularity = useDebounceCallback(async () => {
    if (
      (isMulti(filters.globallySelectedTeams) ||
        isEqualAllTeams(filters.globallySelectedTeams)) &&
      filters.selectedGranularity === GRANULARITY.ITERATIONS
    ) {
      const { selectedTimeRanges } = filters;

      const granularity = getGranularityByDates({
        ...selectedTimeRanges,
        selectedGranularity: GRANULARITY.AUTO
      });

      filters.setSelectedGranularity(granularity);
    } else if (!isMulti(filters.globallySelectedTeams)) {
      // single-team selected
      const updateTeamIterations = async () => {
        const iterations = await getDefaultIterations({
          team_id: filters.firstGloballySelectedTeamId
        });
        setLocalIterations(orderIterations(iterations));
      };
      updateTeamIterations();
    }
  }, 200);

  useEffect(() => {
    updateTeamIterationsAndGranularity();
  }, [filters.globallySelectedTeams]);

  const editableReportDataChange = {
    widtgets: editableReport.definition.widgets.map(({ metrics, filters }) => ({
      metrics,
      filters
    })),
    filters,
    gitScopeToggle
  };

  // Check if we can get agg from calculation_strategy_per_widget so fetchNewMetricsData is triggered when changing agg
  useEffect(() => {
    if (
      !isEqual(
        JSON.stringify(editableReportDataChangeRef.current),
        JSON.stringify(editableReportDataChange)
      ) &&
      timeRange.length
    ) {
      setLoadingMetrics(true);
      fetchNewMetricsData(gitScopeToggle);
      editableReportDataChangeRef.current = editableReportDataChange;
    }
  }, [
    JSON.stringify(filters),
    JSON.stringify(editableReportDataChange),
    JSON.stringify(timeRange)
  ]);

  const openConfigurationModal = (widget, toggleModal) => {
    const onChange = widget => {
      setEditableReport({
        ...editableReport,
        definition: {
          ...editableReport.definition,
          widgets: [...editableReport.definition.widgets, widget]
        }
      });
    };

    const { internal_type: internalType } = widget;
    if (internalType === CUSTOM_DASHBOARD_INTERNAL_TYPES.REPORTED_METRICS) {
      toggleModal(
        reportedMetricsModalOptions({
          widget,
          onChange
        })
      );
    }
  };

  const addTempMetricsToDashboard = metricNames => {
    const widgets = availableMetrics
      .filter(metric => [...metricNames].includes(metric.name))
      .map((widgetItem, index) =>
        isCustomPMMetrics
          ? { ...widgetItem, id: new Date().getTime() + index }
          : widgetItem
      );

    setEditableReport({
      ...editableReport,
      definition: {
        ...editableReport.definition,
        widgets: [...editableReport.definition.widgets, ...widgets]
      }
    });
    return widgets;
  };

  const addMetrics = (toggleModal, isCustomPmMetrics) => {
    const availableMetricsByType = isCustomPmMetrics
      ? availableMetrics.filter(({ category }) =>
          customReportCategories.includes(category)
        )
      : availableMetrics.filter(
          ({ category }) => !customReportCategories.includes(category)
        );
    toggleModal(
      selectMetricsModalOption({
        metrics: isCustomPmMetrics
          ? availableMetricsByType
          : availableMetricsByType.filter(
              metric =>
                !editableReport.definition.widgets
                  .map(({ name }) => name)
                  .includes(metric.name)
            ),
        onDone: metricNames => {
          TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
            page: TRACKING.PAGES.METRICS,
            page_tab:
              editableReport.id === EMPTY_DEFAULT_REPORT.id
                ? TRACKING.PAGE_TABS.DASHBOARD_NEW
                : TRACKING.PAGE_TABS.DASHBOARD_EDIT,
            click_type: TRACKING.CLICK.METRICS_ADDED,
            metric_name: metricNames
          });
          const widgets = addTempMetricsToDashboard(metricNames, toggleModal);
          toggleModal();
          openConfigurationModal(widgets[0], toggleModal);
        },
        isDisableBtn: selectedMetrics => {
          return !selectedMetrics?.size;
        }
      })
    );
  };

  return {
    metrics,
    metricsTotal,
    editableReport,
    editableFilters,
    availableMetrics,
    services,
    loadingMetrics,
    localIterations,
    addMetrics,
    setEditableReport,
    exportMetricsV3Data,
    showAICodeReviewEmptyState
  };
};

export default useCustomReport;
